<script setup lang="ts">

import { computed, ref } from 'vue';

import {
  CustomSelect,
  List,
  useI18n,
  getLanguageName,
} from '@thehotelsnetwork/widgets-core';
import {
  type ReviewItem,
  Locale,
} from '@thehotelsnetwork/personalization-types';

import Review from './Review.vue';

const props = defineProps<{
  reviews: ReviewItem[],
}>();

const { t } = useI18n();

const selectedType = ref('all');
const selectedLanguage = ref('all');
const reviewsEl = ref<HTMLElement>();

const typeOptions = computed(() => {
  const uniqueTypes = [...new Set(props.reviews.map((review) => review.type))];
  const options = uniqueTypes.map((type) => ({
    label: t(type),
    value: type.toLowerCase(),
  }));
  options.unshift({
    label: t('All traveler types'),
    value: 'all',
  });
  return options;
});

const langOptions = computed(() => {
  const langs = [...new Set(props.reviews.map(
    (review) => review.language ?? 'en',
  ))];
  const options = langs.map((language) => ({
    label: getLanguageName(language as Locale),
    value: language.toLowerCase(),
  }));
  options.unshift({
    label: t('All languages'),
    value: 'all',
  });
  return options;
});

const resetListScroll = () => {
  if (reviewsEl.value?.scrollTo) {
    reviewsEl.value.scrollTo(0, 0);
  }
};

const filteredReviews = computed(() => props.reviews.filter((review) => {
  if (selectedType.value === 'all' && selectedLanguage.value === 'all') {
    return true;
  }
  if (selectedType.value === 'all') {
    return review.language.toLowerCase() === selectedLanguage.value;
  }
  if (selectedLanguage.value === 'all') {
    return review.type.toLowerCase() === selectedType.value;
  }
  return review.type.toLowerCase() === selectedType.value
      && review.language.toLowerCase() === selectedLanguage.value;
}));

</script>

<template>
  <div
    class="reviewList"
  >
    <h2 class="reviewList__header">
      {{ t('Highlighted reviews') }}
    </h2>
    <div class="reviewList__filters">
      <CustomSelect
        v-model="selectedType"
        :options="typeOptions"
        class="reviewList__filters--type"
        data-testid="review-type"
        @update:model-value="resetListScroll"
      />
      <CustomSelect
        v-model="selectedLanguage"
        :options="langOptions"
        class="reviewList__filters--language"
        data-testid="review-language"
        @update:model-value="resetListScroll"
      />
    </div>
    <div
      ref="reviewsEl"
      class="reviewList__reviews"
    >
      <List
        v-if="filteredReviews.length > 0"
        :items="filteredReviews"
        data-testid="review-list"
      >
        <template #default="{ item }">
          <Review
            :review="item"
          />
        </template>
      </List>
      <div v-else>
        <p class="reviewList__header mt-2">
          {{ t('No reviews found') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.reviewList {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  min-height: 0;
  text-align: start;
  @apply px-8 py-6;
  &__header {
    @apply pb-4 font-bold text-grey-900 text-xl;
  }
  &__filters {
    @apply flex flex-wrap gap-2 text-sm;
    > * {
      flex: 1 1 auto;
    }
  }
  &__reviews {
    @apply flex-1 overflow-auto -mr-4 pr-4;
  }
}
@container (min-width: 450px) {
  .reviewList__filters > * {
    max-width: 14rem;
  }
}
</style>
